import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router'

import { DATE_FORMAT } from 'common/constants/dateFormatConstants'
import { DateService } from 'common/services/dateService'
import dayjs, { type Dayjs } from 'dayjs'
import {
  SCHEDULE_TAGS,
  scheduleApi,
  useFetchScheduleDayQuery,
  useFetchScheduleMonthQuery,
} from 'features/Schedule/api/schedule.api'
import {
  scheduleGridQueryOptions,
  scheduleProfileQueryOptions,
  scheduleQueryOptions,
} from 'features/Schedule/api/scheduleQueryOptions'
import type { ScheduleMonth } from 'features/Schedule/api/schedule.types'
import { PathUtils } from 'routes/routes.utils'

import { Col, Checkbox, Row } from 'antd'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'

import { Icon } from '@iconify/react'
import { useAuth } from 'app/providers/auth/AuthProvider'
import { useAppDispatch } from 'common/hooks/redux'
import classNames from 'classnames'
import { Button } from 'common/components/Button/Button'
import Popup from 'common/components/Popup'
import Show from 'common/components/Show2/Show'
import { BUTTON_MODIFIER } from 'common/constants/buttonConstants'
import { SESSION_STORAGE_KEYS } from 'common/constants/localeStorageConstants'
import { useTimezone } from 'common/hooks/useTimezone'
import { useScheduleViewPreference } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/useScheduleViewPreference'
import { useScheduleZoom } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/useScheduleZoom'
import { useMultipleChannels } from 'features/Schedule/pusher/MultiplePusherProvider'
import { useSessionStorage } from 'react-use'
import { compareScheduleStaffByRole } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/scheduleInstance'
import { useScheduleRoomsPreference } from 'features/Home/Book/Booking/Schedule/PatientSchedule/ScheduleCalendar/useScheduleRoomsPreference'
import { RoomsOrder } from './components/RoomsOrder/RoomsOrder'
import { ScheduleDayView } from './components/ScheduleDayView/ScheduleDayView'
import { ScheduleLoading } from './components/ScheduleLoading/ScheduleLoading'
import { ScheduleWeekView } from './components/ScheduleWeekView/ScheduleWeekView'
import styles from './schedule.module.scss'
import ScheduleHeaderCalendar from 'common/components/ScheduleHeaderCalendar/ScheduleHeaderCalendar'
import { useFetchProfileQuery } from 'features/SettingsOld/state/api/settingsApi'
import { INFO_CONSTANTS } from 'features/Home/constants/infoConstants'

const { getCurrentTimeInTimezone, preserveDateInTimezone } = DateService

dayjs.extend(isSameOrAfter)
dayjs.extend(customParseFormat)

export enum EScheduleView {
  DAY = 'day',
  WEEK = 'week',
}

const MAX_ACCUMULATED_SCHEDULE_DAYS = 400

const SchedulePage = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { setBookingIds } = useMultipleChannels()

  const { siteId, day } = useParams()
  const [calendarMonthDate, setCalendarMonthDate] = useState<string | null>(null)
  const { timeZone, isFetchingSites } = useTimezone()

  const viewDate = useMemo(() => {
    if (!timeZone) return null

    if (day && dayjs(day).isValid()) {
      return preserveDateInTimezone(day, timeZone).format(DATE_FORMAT.PATIENT_TRACKER_DATE)
    }

    return getCurrentTimeInTimezone(timeZone).format(DATE_FORMAT.PATIENT_TRACKER_DATE)
  }, [day, timeZone])

  const { data: profile } = useFetchProfileQuery(null, scheduleProfileQueryOptions)

  const [accumulatedScheduleData, setAccumulatedScheduleData] = useState<ScheduleMonth>({})

  const { hasEmrEnabled } = useAuth()

  const { currentView, setCurrentView } = useScheduleViewPreference()

  const isDayView = currentView === EScheduleView.DAY
  const isWeekView = !isDayView

  const [showCanceled, setShowCanceled] = useSessionStorage<boolean>(
    SESSION_STORAGE_KEYS.SHOW_CANCELED_SCHEDULES,
    true,
  )

  const { zoomInterval, zoomIn: handleZoomIn, zoomOut: handleZoomOut } = useScheduleZoom()

  const scheduleMonthQueryArgs = useMemo(() => {
    if (!siteId || !timeZone) return null

    const dateSource = calendarMonthDate ?? day
    const date = dateSource
      ? preserveDateInTimezone(dateSource, timeZone)
      : getCurrentTimeInTimezone(timeZone)

    return {
      siteId,
      startDate: date.startOf('month').add(-1, 'month').format(DATE_FORMAT.PATIENT_TRACKER_DATE),
      endDate: date.add(6, 'month').endOf('month').format(DATE_FORMAT.PATIENT_TRACKER_DATE),
    }
  }, [siteId, timeZone, calendarMonthDate, day])

  const {
    currentData: scheduleMonth,
    isLoading: isLoadingMonth,
    isFetching: isFetchingScheduleMonth,
  } = useFetchScheduleMonthQuery(scheduleMonthQueryArgs!, {
    skip: !scheduleMonthQueryArgs,
    ...scheduleQueryOptions,
  })

  const todayDate = useMemo(() => {
    if (!timeZone) return null
    return getCurrentTimeInTimezone(timeZone).format(DATE_FORMAT.PATIENT_TRACKER_DATE)
  }, [timeZone])

  const { currentData: scheduleDay } = useFetchScheduleDayQuery(
    { siteId, date: viewDate! },
    {
      skip: !siteId || !viewDate,
      ...scheduleGridQueryOptions,
    },
  )

  // Response flag first; profile flag while the day loads.
  const hasEhrBooking = Boolean(scheduleDay?.has_ehr_booking ?? hasEmrEnabled)

  const isFutureDayView = Boolean(isDayView && viewDate && todayDate && viewDate > todayDate)

  // OE keeps the full future-day padding; non-OE gets half of it on every day and week view.
  const paddedPageClassName = hasEhrBooking
    ? isFutureDayView && styles.paddedPage
    : styles.paddedPageCompact

  const pendingTodayDayViewRef = useRef(false)

  const isPageLoading = isFetchingSites || !viewDate || isLoadingMonth

  const scheduleRooms = useMemo(() => scheduleDay?.rooms ?? [], [scheduleDay?.rooms])
  const {
    roomOptions,
    roomOrder,
    hasSavedRoomOrder,
    hiddenRoomIds,
    visibleRoomIds,
    onRoomsChange,
  } = useScheduleRoomsPreference(scheduleRooms)
  const dialogRoomOrder = hasEmrEnabled && hasSavedRoomOrder ? roomOrder : null

  const doctorsUsers = useMemo(
    () =>
      (profile?.users?.filter((user) => user.is_doctor || user.is_provider) ?? []).sort(
        compareScheduleStaffByRole,
      ),
    [profile?.users],
  )

  useEffect(() => {
    setAccumulatedScheduleData({})
  }, [siteId])

  useEffect(() => {
    if (!scheduleMonth) return

    setAccumulatedScheduleData((prevData) => {
      const merged: ScheduleMonth = { ...prevData, ...scheduleMonth }
      const keys = Object.keys(merged).sort()

      if (keys.length <= MAX_ACCUMULATED_SCHEDULE_DAYS) {
        return merged
      }

      // keep the days closest to the freshly fetched window so it always survives the trim
      const anchor = Object.keys(scheduleMonth).sort()[0] ?? keys[0]
      const anchorTime = dayjs(anchor).valueOf()
      const keepKeys = new Set(
        [...keys]
          .sort(
            (a, b) =>
              Math.abs(dayjs(a).valueOf() - anchorTime) - Math.abs(dayjs(b).valueOf() - anchorTime),
          )
          .slice(0, MAX_ACCUMULATED_SCHEDULE_DAYS),
      )
      return Object.fromEntries(Object.entries(merged).filter(([key]) => keepKeys.has(key)))
    })
  }, [scheduleMonth])

  const allDays = useMemo(() => {
    if (!accumulatedScheduleData || Object.keys(accumulatedScheduleData).length === 0) return []
    return Object.keys(accumulatedScheduleData).sort()
  }, [accumulatedScheduleData])

  const availableDays = useMemo(() => {
    return allDays.filter((key) => accumulatedScheduleData[key]?.active)
  }, [allDays, accumulatedScheduleData])

  useEffect(() => {
    if (!timeZone || !siteId) return
    if ((!day || !dayjs(day).isValid()) && viewDate) {
      navigate(PathUtils.getSchedule(siteId, viewDate), { replace: true })
    }
  }, [day, timeZone, siteId, viewDate, navigate])

  const onChangeLeaseDate = useCallback(
    (date: string): void => {
      if (!timeZone || !siteId) return

      const formattedDate = preserveDateInTimezone(date, timeZone).format(
        DATE_FORMAT.PATIENT_TRACKER_DATE,
      )

      if (day !== formattedDate) {
        navigate(PathUtils.getSchedule(siteId, formattedDate))
      }
    },
    [siteId, timeZone, day, navigate],
  )

  const openDayView = useCallback(() => {
    setCurrentView(EScheduleView.DAY)
  }, [setCurrentView])

  const handleToday = useCallback(() => {
    if (!todayDate) return

    // Wait until the URL is today before mounting day view. Switching first
    // binds Syncfusion to the previous date's dataSource, then remounts on
    // today's selectedDate and the appointments never paint until a refresh.
    if (viewDate === todayDate) {
      openDayView()
      return
    }

    pendingTodayDayViewRef.current = true
    onChangeLeaseDate(todayDate)
  }, [todayDate, viewDate, openDayView, onChangeLeaseDate])

  useEffect(() => {
    if (!pendingTodayDayViewRef.current || !viewDate || !todayDate) return
    if (viewDate !== todayDate) return
    pendingTodayDayViewRef.current = false
    openDayView()
  }, [viewDate, todayDate, openDayView])

  const handleBook = () => {
    navigate(PathUtils.getBook(siteId))
  }

  const handleChangeWeek = (date: Dayjs) => {
    if (isWeekView) {
      onChangeLeaseDate(date.format(DATE_FORMAT.PATIENT_TRACKER_DATE))
    }
  }

  const handleWarningPopup = () => {
    Popup.showInfo(
      {
        title: 'No Booking Found!',
        message: INFO_CONSTANTS.NO_ACTIVE_SCHEDULE_TODAY,
        mainButtonText: 'Book',
      },
      () => handleBook(),
    )
  }

  const doctorsIds = useMemo(() => doctorsUsers.map((staff) => String(staff.id)), [doctorsUsers])

  useEffect(() => {
    if (doctorsIds.length) {
      setBookingIds(doctorsIds)
    }
  }, [doctorsIds, setBookingIds])

  useEffect(() => {
    return () => {
      dispatch(
        scheduleApi.util.invalidateTags([SCHEDULE_TAGS.SCHEDULE, SCHEDULE_TAGS.SCHEDULE_WEEK]),
      )
    }
  }, [dispatch])

  if (isPageLoading) {
    return (
      <Row
        className={classNames(styles.rowContainer, paddedPageClassName)}
        data-schedule-root='true'>
        <Col xs={24}>
          <ScheduleLoading />
        </Col>
      </Row>
    )
  }

  return (
    <Row className={classNames(styles.rowContainer, paddedPageClassName)} data-schedule-root='true'>
      <Col xs={24} className={styles.headerCol}>
        <Row
          id='schedule-header-calendar'
          className={styles.headerRow}
          justify={{ sm: 'start', xs: 'center' }}
          align={{ md: 'middle', xs: 'top' }}>
          <Col xs={24}>
            <div className={styles.headerContainer}>
              <ScheduleHeaderCalendar
                onOpenChange={(open) => {
                  if (!open) {
                    setCalendarMonthDate(null)
                  }
                }}
                isFetching={isFetchingScheduleMonth}
                isLoading={false}
                defaultActiveDate={viewDate}
                availableDays={availableDays}
                scheduleData={accumulatedScheduleData}
                isWeekView={isWeekView}
                onDateChange={(date) => {
                  onChangeLeaseDate(date)
                }}
                handleClickDisabledDate={handleWarningPopup}
                onChangeMonth={(date) => {
                  setCalendarMonthDate(date.format(DATE_FORMAT.PATIENT_TRACKER_DATE))
                }}
                onChangeWeek={handleChangeWeek}
                timeZone={timeZone}
                forceEnableFutureDate
                forceEnablePastDate
              />

              <div className={styles.calendarViewSwitchContainer}>
                <Button
                  modifier={isDayView ? BUTTON_MODIFIER.PRIMARY : BUTTON_MODIFIER.DEFAULT}
                  className={classNames(
                    styles.calendarViewSwitch,
                    styles['calendarViewSwitch--not-selected'],
                  )}
                  onClick={handleToday}>
                  Today
                </Button>
                <Button
                  modifier={isDayView ? BUTTON_MODIFIER.PRIMARY : BUTTON_MODIFIER.DEFAULT}
                  className={classNames(
                    styles.calendarViewSwitch,
                    !isDayView && styles['calendarViewSwitch--not-selected'],
                  )}
                  onClick={() => {
                    pendingTodayDayViewRef.current = false
                    setCurrentView(EScheduleView.DAY)
                  }}>
                  Day
                </Button>
                <Button
                  modifier={isWeekView ? BUTTON_MODIFIER.PRIMARY : BUTTON_MODIFIER.DEFAULT}
                  className={classNames(
                    styles.calendarViewSwitch,
                    !isWeekView && styles['calendarViewSwitch--not-selected'],
                  )}
                  onClick={() => {
                    pendingTodayDayViewRef.current = false
                    setCurrentView(EScheduleView.WEEK)
                  }}>
                  Week
                </Button>
                <div className={styles.zoomControls}>
                  <Button className={styles.zoomButton} onClick={handleZoomOut}>
                    <Icon icon='ic:round-minus' width={18} height={18} />
                  </Button>
                  <Button className={styles.zoomButton} onClick={handleZoomIn}>
                    <Icon icon='ic:round-plus' width={18} height={18} />
                  </Button>
                </div>
                {isDayView && hasEmrEnabled && (
                  <RoomsOrder
                    rooms={roomOptions}
                    order={roomOrder}
                    hidden={hiddenRoomIds}
                    onChange={onRoomsChange}
                    disabled={!roomOptions.length}
                  />
                )}
                <Checkbox
                  checked={showCanceled ?? true}
                  onChange={(e) => setShowCanceled(e.target.checked)}
                  className={styles.showCanceledCheckbox}>
                  Show canceled
                </Checkbox>
              </div>
            </div>
          </Col>
        </Row>
      </Col>

      <Show>
        <Show.When isVisible={isDayView}>
          <ScheduleDayView
            defaultActiveDate={viewDate}
            selectedConsultationStaffIds={null}
            selectedRoomIds={hasEmrEnabled ? visibleRoomIds : null}
            roomOrder={dialogRoomOrder}
            showCanceled={showCanceled ?? true}
            scheduleView={EScheduleView.DAY}
            scheduleMonthData={accumulatedScheduleData}
            zoomInterval={zoomInterval}
            staffRoleUsers={profile?.users}
          />
        </Show.When>
        <Show.Else>
          <ScheduleWeekView
            defaultActiveDate={viewDate}
            selectedConsultationStaffIds={null}
            roomOrder={dialogRoomOrder}
            showCanceled={showCanceled ?? true}
            scheduleView={EScheduleView.WEEK}
            scheduleMonthData={accumulatedScheduleData}
            zoomInterval={zoomInterval}
            staffRoleUsers={profile?.users}
          />
        </Show.Else>
      </Show>
    </Row>
  )
}

export default SchedulePage
